import { memo, useEffect, useState } from "react";

// Components
import HeaderFormDetail from "./HeaderFormDetail";
import TwoPanelSplitView from "./TwoPanelSplitView";
import CardMeFormLeafNode from "./CardMeFormLeafNode";
import CardMeFormEditContent from "./CardMeFormEditContent";

// Serializers
import DiagRuleSerializer from "@/issara-sdk/types/DiagRuleSerializer_apps_PRX";
import PublishedDiagRuleNodeSerializer from "@/issara-sdk/types/PublishedDiagRuleNodeSerializer_apps_PRX";

// Styles
import "./styles/CardMeFormDetail.scss";

// Types
import {
  FlatNode,
  TreeNode,
  RunSequence,
  SetProp,
  PickedState,
  PatientByLeafList,
  BroadcastNodeList
} from "./MeFormType";

type CardDecisionTreeDetailProps = {
  // data
  formDetail?: DiagRuleSerializer;
  pdrNodeList: PublishedDiagRuleNodeSerializer[];
  broadcastNodeList: BroadcastNodeList;
  patientByLeafList?: PatientByLeafList;

  // tree
  selectedNode: TreeNode | null;
  onSelectNode: (node: TreeNode | null) => void;

  // permissions / flags
  isPublished: boolean;
  isActive: boolean;

  // Infra
  runSequence: RunSequence;
  setProp: SetProp;
  languageUX: "en" | "th";
  selectedDivision: any;

  // actions
  onGoBack: () => void;
  onEditForm: () => void;
  onPublishForm: () => void;
  onDuplicateForm: () => void;
  onOpenPostBroadcast: (node: FlatNode | TreeNode | null) => void;
} & Pick<PickedState, "buttonLoadCheck" | "loadingStatus" | "BillingCreditWallet">;

const CardDecisionTreeDetail = (props: CardDecisionTreeDetailProps) => {
  const { formDetail } = props;

  const [formContent, setFormContent] = useState<string>(formDetail?.content ?? "");

  useEffect(() => {
    setFormContent(formDetail?.content ?? "");
  }, [formDetail]);

  return (
    <div className="card-meform-detail">
      <HeaderFormDetail
        // data
        formDetail={formDetail}

        // permissions / flags
        isActive={props.isActive}
        isPublished={props.isPublished}

        // Infra
        setProp={props.setProp}
        selectedDivision={props.selectedDivision}

        // Common Infra
        buttonLoadCheck={props.buttonLoadCheck}

        // actions
        onGoBack={props.onGoBack}
        onEdit={props.onEditForm}
        onDuplicate={props.onDuplicateForm}
        onPublish={props.onPublishForm}
      />

      <TwoPanelSplitView
        leftPanel={
          <CardMeFormLeafNode
            runSequence={props.runSequence}
            // data
            isPublished={props.isPublished}
            content={formContent}
            pdrNodeList={props.pdrNodeList}
            selectedNode={props.selectedNode}
            // func
            onSelectNode={props.onSelectNode}
            onChangeContent={setFormContent}
          />
        }
        rightPanel={
          props.selectedNode && (
            <div>
              <CardMeFormEditContent
                // func
                runSequence={props.runSequence}
                setProp={props.setProp}
                // data
                isActive={props.isActive}
                isPublished={props.isPublished}
                detail={formDetail}
                content={formContent}
                selectedNode={props.selectedNode}
                pdrList={props.pdrNodeList}
                broadcastNodeList={props.broadcastNodeList}
                onChangeContent={setFormContent}
                onOpenPostBroadcast={props.onOpenPostBroadcast}
                // common
                buttonLoadCheck={props.buttonLoadCheck}
                BillingCreditWallet={props.BillingCreditWallet}
                loadingStatus={props.loadingStatus}
              />
            </div>
          )
        }
        // config
        defaultLeftWidth={50}
        // onResize={(left, right) => console.log(`[Panel Size] Left: ${left}, Right: ${right}`)}
      />
    </div>
  );
};

export default memo(CardDecisionTreeDetail);